/**
 * PlanHistory — the change/audit timeline for one B2C plan.
 *
 * Fed by GET /b2c/admin/plans/{id}/history (plan_history_service): one event per
 * mutating admin action — created / details updated / enabled / disabled /
 * set-default / unset-default / rate-card imported / rate-card edited — newest
 * first. Scalar field edits render as "field: from → to"; rate-card changes
 * render as a per-row diff (added / removed / modified with the changed cells).
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Loader2, History as HistoryIcon } from "lucide-react";

const ACTION_META = {
  created: { label: "Created", cls: "bg-green-100 text-green-800" },
  updated: { label: "Details updated", cls: "bg-blue-100 text-blue-800" },
  enabled: { label: "Enabled", cls: "bg-green-100 text-green-800" },
  disabled: { label: "Disabled", cls: "bg-slate-200 text-slate-700" },
  set_default: { label: "Set as default", cls: "bg-amber-100 text-amber-800" },
  unset_default: { label: "Unset as default", cls: "bg-slate-200 text-slate-700" },
  rate_card_imported: { label: "Rate card imported", cls: "bg-indigo-100 text-indigo-800" },
  rate_card_edited: { label: "Rate card edited", cls: "bg-indigo-100 text-indigo-800" },
};

const FIELD_LABELS = {
  name: "Name",
  monthly_fee: "Monthly fee",
  fee_refund_at_shipments: "Fee refund at",
  cod_cycle_days: "COD cycle",
  is_default: "Default",
  status: "Status",
  incr_unit_kg: "Incr unit",
  cod_flat: "COD flat",
  cod_pct: "COD %",
};

const ZONE_PREFIX = { fwd: "Fwd", fwd_incr: "Fwd incr", rto: "RTO", rto_incr: "RTO incr" };

// "fwd.A" -> "Fwd A", "rto_incr.D" -> "RTO incr D", scalar -> its label.
const cellLabel = (field) => {
  if (field.includes(".")) {
    const [grp, zone] = field.split(".");
    return `${ZONE_PREFIX[grp] || grp} ${zone}`;
  }
  return FIELD_LABELS[field] || field;
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

const fmtWhen = (at) => {
  if (!at) return "";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return String(at);
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Non-null forward zones of an added/removed row, compact: "A 180 · B 200".
const fwdSummary = (row) => {
  const fwd = row?.fwd || {};
  const parts = ["A", "B", "C", "D", "E", "F"]
    .filter((z) => fwd[z] !== null && fwd[z] !== undefined)
    .map((z) => `${z} ${fwd[z]}`);
  return parts.length ? parts.join(" · ") : "no forward rates";
};

const ROW_DETAIL_CAP = 40;

function RateCardDiff({ diff }) {
  const [open, setOpen] = useState(false);
  if (!diff) return null;
  const { added = [], removed = [], modified = [], added_count = 0, removed_count = 0, modified_count = 0 } = diff;

  const summaryBits = [];
  if (added_count) summaryBits.push(`${added_count} added`);
  if (removed_count) summaryBits.push(`${removed_count} removed`);
  if (modified_count) summaryBits.push(`${modified_count} modified`);

  const rowId = (r) => `${r.courier_code} · ${r.mode} · ${r.slab_kg}kg`;
  const capNote = (n) => (n > ROW_DETAIL_CAP ? ` (showing first ${ROW_DETAIL_CAP} of ${n})` : "");

  return (
    <div className="mt-2 rounded-md bg-slate-50 p-2.5 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-slate-600">Rate card: {summaryBits.join(" · ") || "no changes"}</span>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-indigo-600 hover:underline"
          data-testid="rc-diff-toggle"
        >
          {open ? "Hide" : "Show"} rows
        </button>
      </div>
      {open && (
        <div className="mt-2 space-y-2">
          {modified.length > 0 && (
            <div>
              <p className="font-medium text-slate-500">Modified{capNote(modified.length)}</p>
              {modified.slice(0, ROW_DETAIL_CAP).map((r, i) => (
                <div key={`m${i}`} className="mt-1">
                  <span className="font-medium text-slate-700">{rowId(r)}</span>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {(r.cells || []).map((c, j) => (
                      <span key={j} className="rounded bg-white px-1.5 py-0.5 tabular-nums text-slate-600 ring-1 ring-slate-200">
                        {cellLabel(c.field)} {fmtVal(c.from)}→{fmtVal(c.to)}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {added.length > 0 && (
            <div>
              <p className="font-medium text-green-700">Added{capNote(added.length)}</p>
              {added.slice(0, ROW_DETAIL_CAP).map((r, i) => (
                <div key={`a${i}`} className="mt-0.5 text-slate-600">
                  <span className="font-medium text-slate-700">{rowId(r)}</span>
                  <span className="ml-1.5 text-slate-400 tabular-nums">{fwdSummary(r)}</span>
                </div>
              ))}
            </div>
          )}
          {removed.length > 0 && (
            <div>
              <p className="font-medium text-red-700">Removed{capNote(removed.length)}</p>
              {removed.slice(0, ROW_DETAIL_CAP).map((r, i) => (
                <div key={`r${i}`} className="mt-0.5 text-slate-500 line-through">
                  {rowId(r)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PlanHistory({ planId }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!planId) return;
    let alive = true;
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/b2c/admin/plans/${planId}/history`)
      .then((res) => {
        if (alive) setRows(res.data || []);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [planId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
      </div>
    );
  }
  if (error) {
    return <p className="py-8 text-center text-sm text-slate-500">Could not load plan history.</p>;
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <HistoryIcon className="h-8 w-8 text-slate-300" strokeWidth={1.5} />
        <p className="text-sm text-slate-500">No changes recorded yet.</p>
        <p className="text-xs text-slate-400">Edits from here on will be logged.</p>
      </div>
    );
  }

  return (
    <ol className="space-y-3" data-testid="plan-history-list">
      {rows.map((ev) => {
        const meta = ACTION_META[ev.action] || { label: ev.action, cls: "bg-slate-100 text-slate-700" };
        const actor = ev.actor || {};
        const who = actor.user_name || actor.user_email || "Unknown";
        const verChanged = ev.version_from != null && ev.version_to != null && ev.version_from !== ev.version_to;
        return (
          <li key={ev.id} className="rounded-lg border bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded px-2 py-0.5 text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
              {verChanged && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs tabular-nums text-slate-600">
                  v{ev.version_from} → v{ev.version_to}
                </span>
              )}
              <span className="ml-auto text-xs text-slate-400 tabular-nums">{fmtWhen(ev.at)}</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {who}
              {actor.role ? ` · ${actor.role}` : ""}
            </p>

            {Array.isArray(ev.changes) && ev.changes.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {ev.changes.map((c, i) => (
                  <span key={i} className="rounded bg-slate-50 px-1.5 py-0.5 text-xs tabular-nums text-slate-600 ring-1 ring-slate-200">
                    {cellLabel(c.field)}: {fmtVal(c.from)} → {fmtVal(c.to)}
                  </span>
                ))}
              </div>
            )}

            {ev.rate_card_diff && <RateCardDiff diff={ev.rate_card_diff} />}

            {ev.note && <p className="mt-1.5 text-xs italic text-slate-400">{ev.note}</p>}
          </li>
        );
      })}
    </ol>
  );
}

export default PlanHistory;
